import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { updateTaxes, updateCompanyApi, updateSocialStatuses, updateWithholdingKinds, listLocations, createLocationApi, updateLocationApi, deleteLocationApi } from "../api";
import { computeTaxes, resolveRefs } from "../controllers/taxes";
import { DEFAULT_STATUSES } from "../constants";
import { allWithholdingKindDefs } from "../controllers/withholdings";
import AppPage from "../components/AppPage";

const TAX_DEFAULTS = {
  ipn: 10, opv: 10, vosms: 2, so: 3.5,
  useMzpDeduction: true, useOpvDeduction: true, useVosmsDeduction: true, mzpAmount: 85000,
};

const PRESET_COLORS = ["#198754", "#dc3545", "#ffc107", "#0dcaf0", "#6f42c1", "#20c997",
                       "#fd7e14", "#0b3d91", "#e83e8c", "#6c757d"];

// IANA timezone presets relevant to KZ + CIS market.
// Grouped roughly by UTC offset for easier scanning.
const TIMEZONES = [
  { value: "Asia/Almaty",        label: "Asia/Almaty",         offset: "UTC+5", country: "🇰🇿" },
  { value: "Asia/Aqtau",         label: "Asia/Aqtau",          offset: "UTC+5", country: "🇰🇿" },
  { value: "Asia/Aqtobe",        label: "Asia/Aqtobe",         offset: "UTC+5", country: "🇰🇿" },
  { value: "Asia/Atyrau",        label: "Asia/Atyrau",         offset: "UTC+5", country: "🇰🇿" },
  { value: "Asia/Oral",          label: "Asia/Oral",           offset: "UTC+5", country: "🇰🇿" },
  { value: "Asia/Qostanay",      label: "Asia/Qostanay",       offset: "UTC+5", country: "🇰🇿" },
  { value: "Asia/Qyzylorda",     label: "Asia/Qyzylorda",      offset: "UTC+5", country: "🇰🇿" },
  { value: "Asia/Bishkek",       label: "Asia/Bishkek",        offset: "UTC+6", country: "🇰🇬" },
  { value: "Asia/Tashkent",      label: "Asia/Tashkent",       offset: "UTC+5", country: "🇺🇿" },
  { value: "Asia/Dushanbe",      label: "Asia/Dushanbe",       offset: "UTC+5", country: "🇹🇯" },
  { value: "Asia/Ashgabat",      label: "Asia/Ashgabat",       offset: "UTC+5", country: "🇹🇲" },
  { value: "Asia/Baku",          label: "Asia/Baku",           offset: "UTC+4", country: "🇦🇿" },
  { value: "Asia/Yerevan",       label: "Asia/Yerevan",        offset: "UTC+4", country: "🇦🇲" },
  { value: "Asia/Tbilisi",       label: "Asia/Tbilisi",        offset: "UTC+4", country: "🇬🇪" },
  { value: "Europe/Moscow",      label: "Europe/Moscow",       offset: "UTC+3", country: "🇷🇺" },
  { value: "Europe/Kaliningrad", label: "Europe/Kaliningrad",  offset: "UTC+2", country: "🇷🇺" },
  { value: "Asia/Yekaterinburg", label: "Asia/Yekaterinburg",  offset: "UTC+5", country: "🇷🇺" },
  { value: "Asia/Novosibirsk",   label: "Asia/Novosibirsk",    offset: "UTC+7", country: "🇷🇺" },
  { value: "Asia/Krasnoyarsk",   label: "Asia/Krasnoyarsk",    offset: "UTC+7", country: "🇷🇺" },
  { value: "Asia/Irkutsk",       label: "Asia/Irkutsk",        offset: "UTC+8", country: "🇷🇺" },
  { value: "Asia/Vladivostok",   label: "Asia/Vladivostok",    offset: "UTC+10", country: "🇷🇺" },
  { value: "Europe/Minsk",       label: "Europe/Minsk",        offset: "UTC+3", country: "🇧🇾" },
  { value: "Europe/Kyiv",        label: "Europe/Kyiv",         offset: "UTC+2", country: "🇺🇦" },
  { value: "UTC",                label: "UTC",                 offset: "UTC+0", country: "🌐" },
];

function slugifyKey(s) {
  return (s || "").toString().toLowerCase()
    .replace(/[а-я]/g, c => ({а:'a',б:'b',в:'v',г:'g',д:'d',е:'e',ё:'e',ж:'zh',з:'z',и:'i',й:'j',к:'k',л:'l',м:'m',н:'n',о:'o',п:'p',р:'r',с:'s',т:'t',у:'u',ф:'f',х:'h',ц:'c',ч:'ch',ш:'sh',щ:'sh',ы:'y',э:'e',ю:'u',я:'ya',ь:'',ъ:''}[c] || c))
    .replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 32) || `s_${Date.now()}`;
}

export default function Settings() {
  const { t } = useTranslation();
  const { company, refresh } = useAuth();
  const [tab, setTab] = useState("general");

  return (
    <AppPage testId="settings-page" bodyScroll="scroll">
      <h4 className="fw-bold mb-3">{t("settings.title")}</h4>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === "general" ? "active" : ""}`}
                  onClick={() => setTab("general")} data-testid="tab-general">
            <i className="bi bi-building me-1"></i> {t("settings.tab_general")}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === "statuses" ? "active" : ""}`}
                  onClick={() => setTab("statuses")} data-testid="tab-statuses">
            <i className="bi bi-tags me-1"></i> {t("settings.tab_statuses")}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === "taxes" ? "active" : ""}`}
                  onClick={() => setTab("taxes")} data-testid="tab-taxes">
            <i className="bi bi-receipt me-1"></i> {t("settings.tab_taxes")}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === "locations" ? "active" : ""}`}
                  onClick={() => setTab("locations")} data-testid="tab-locations">
            <i className="bi bi-geo-alt me-1"></i> {t("settings.tab_locations")}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === "social" ? "active" : ""}`}
                  onClick={() => setTab("social")} data-testid="tab-social-statuses">
            <i className="bi bi-person-badge me-1"></i> {t("settings.tab_social_statuses")}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === "withholding_kinds" ? "active" : ""}`}
                  onClick={() => setTab("withholding_kinds")} data-testid="tab-withholding-kinds">
            <i className="bi bi-dash-circle me-1"></i> {t("settings.tab_withholding_kinds")}
          </button>
        </li>
      </ul>

      {tab === "general" && <GeneralTab company={company} refresh={refresh} />}
      {tab === "statuses" && <StatusesTab company={company} refresh={refresh} />}
      {tab === "taxes" && <TaxesTab company={company} refresh={refresh} />}
      {tab === "locations" && <LocationsTab />}
      {tab === "social" && <SocialStatusesTab company={company} refresh={refresh} />}
      {tab === "withholding_kinds" && <WithholdingKindsTab company={company} refresh={refresh} />}
    </AppPage>
  );
}

function GeneralTab({ company, refresh }) {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState(company?.name || "");
  const [weekType, setWeekType] = useState(company?.default_week_type || 5);
  const [tz, setTz] = useState(company?.timezone || "Asia/Almaty");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setName(company?.name || "");
    setWeekType(company?.default_week_type || 5);
    setTz(company?.timezone || "Asia/Almaty");
  }, [company]);

  // Live tick once per second for the "current time" preview
  useEffect(() => {
    const id = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const localeMap = { ru: "ru-RU", kk: "kk-KZ", en: "en-US" };
  const locale = localeMap[i18n.language] || "ru-RU";
  let tzPreview = "";
  try {
    tzPreview = new Intl.DateTimeFormat(locale, {
      timeZone: tz, dateStyle: "medium", timeStyle: "medium",
    }).format(new Date());
  } catch {
    tzPreview = new Date().toLocaleString(locale);
  }
  // tick is used to force re-render; reference it to satisfy lint
  void tick;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(""); setErr("");
    try {
      await updateCompanyApi({
        name: name.trim(),
        default_week_type: Number(weekType),
        timezone: tz,
      });
      await refresh();
      setMsg(t("settings.saved"));
    } catch (e2) {
      setErr(e2.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="card card-soft" data-testid="settings-general"><div className="card-body">
      {msg && <div className="alert alert-success py-2">{msg}</div>}
      {err && <div className="alert alert-danger py-2">{err}</div>}
      <form onSubmit={save} className="row g-3">
        <div className="col-md-6">
          <label className="form-label small">{t("settings.general.company_name")}</label>
          <input className="form-control" required value={name}
                 onChange={(e) => setName(e.target.value)} data-testid="settings-name" />
        </div>
        <div className="col-md-3">
          <label className="form-label small">{t("settings.general.default_week")}</label>
          <select className="form-select" value={weekType}
                  onChange={(e) => setWeekType(e.target.value)} data-testid="settings-week">
            <option value={5}>{t("settings.general.week_5")}</option>
            <option value={6}>{t("settings.general.week_6")}</option>
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label small">
            <i className="bi bi-globe me-1"></i>{t("settings.general.timezone")}
          </label>
          <select className="form-select" value={tz}
                  onChange={(e) => setTz(e.target.value)} data-testid="settings-timezone">
            {TIMEZONES.map(z => (
              <option key={z.value} value={z.value}>
                {z.country} {z.label} · {z.offset}
              </option>
            ))}
          </select>
          <div className="form-text small"
               dangerouslySetInnerHTML={{ __html: t("settings.general.timezone_hint", { time: tzPreview }) }} />
        </div>
        <div className="col-12 small text-muted">
          <i className="bi bi-info-circle me-1"></i>
          {t("settings.general.hint")}
        </div>
        <div className="col-12">
          <button className="btn btn-primary" disabled={busy} data-testid="settings-save-general">
            <i className="bi bi-check-circle me-1"></i> {t("settings.general.save")}
          </button>
        </div>
      </form>
    </div></div>
  );
}

function StatusesTab({ company, refresh }) {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    const initial = (company?.statuses?.length ? company.statuses : DEFAULT_STATUSES);
    setList(initial.map(s => ({ ...s })));
  }, [company]);

  const onChange = (idx, field, value) => {
    const next = [...list];
    next[idx] = { ...next[idx], [field]: value };
    setList(next);
  };

  const onAdd = () => {
    setList([...list, {
      key: `s_${Date.now()}`,
      label: t("settings.statuses.new_status"),
      short: "Н",
      color: PRESET_COLORS[list.length % PRESET_COLORS.length],
      paid_factor: 1.0,
      pay_type: "coefficient",
      fixed_amount: 0,
    }]);
  };

  const onRemove = (idx) => {
    if (list.length <= 1) { setErr(t("settings.statuses.at_least_one")); return; }
    setList(list.filter((_, i) => i !== idx));
  };

  const onResetDefaults = () => setList(DEFAULT_STATUSES.map(s => ({ ...s })));

  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const cleaned = list.map(s => {
        const payType = s.pay_type === "fixed" ? "fixed" : "coefficient";
        return {
          key: (s.key && s.key.trim()) ? s.key.trim() : slugifyKey(s.label),
          label: (s.label || "").trim() || t("settings.statuses.untitled"),
          short: (s.short || "").trim().slice(0, 6) || "?",
          color: s.color || "#6c757d",
          pay_type: payType,
          paid_factor: payType === "fixed" ? 0 : Math.max(0, Math.min(2, Number(s.paid_factor) || 0)),
          fixed_amount: payType === "fixed" ? Math.max(0, Number(s.fixed_amount) || 0) : 0,
        };
      });
      const keys = cleaned.map(s => s.key);
      if (new Set(keys).size !== keys.length) {
        setErr(t("settings.statuses.unique_keys"));
        return;
      }
      await updateCompanyApi({ statuses: cleaned });
      await refresh();
      setMsg(t("settings.statuses.saved"));
    } catch (e2) {
      setErr(e2.response?.data?.detail || t("common.save_error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="card card-soft" data-testid="settings-statuses"><div className="card-body">
      {msg && <div className="alert alert-success py-2">{msg}</div>}
      {err && <div className="alert alert-danger py-2">{err}</div>}

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="small text-muted" dangerouslySetInnerHTML={{ __html: t("settings.statuses.hint") }} />
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={onResetDefaults} data-testid="status-reset">
            <i className="bi bi-arrow-clockwise me-1"></i> {t("settings.statuses.reset_defaults")}
          </button>
          <button className="btn btn-primary btn-sm" onClick={onAdd} data-testid="status-add">
            <i className="bi bi-plus-lg me-1"></i> {t("settings.statuses.add")}
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ width: 40 }}></th>
              <th>{t("settings.statuses.th_label")}</th>
              <th style={{ width: 90 }}>{t("settings.statuses.th_short")}</th>
              <th style={{ width: 130 }}>{t("settings.statuses.th_color")}</th>
              <th style={{ width: 170 }}>{t("settings.statuses.th_paytype")}</th>
              <th style={{ width: 170 }}>{t("settings.statuses.th_pay")}</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {list.map((s, idx) => {
              const payType = s.pay_type === "fixed" ? "fixed" : "coefficient";
              return (
              <tr key={idx} data-testid={`status-row-${idx}`}>
                <td>
                  <span className="status-pill" style={{ background: s.color }}>{s.short || "?"}</span>
                </td>
                <td>
                  <input className="form-control form-control-sm" value={s.label}
                         onChange={(e) => onChange(idx, "label", e.target.value)}
                         data-testid={`status-label-${idx}`} />
                </td>
                <td>
                  <input className="form-control form-control-sm text-center" maxLength={6} value={s.short}
                         onChange={(e) => onChange(idx, "short", e.target.value)} />
                </td>
                <td>
                  <div className="d-flex align-items-center gap-1">
                    <input type="color" className="form-control form-control-color form-control-sm"
                           value={s.color} onChange={(e) => onChange(idx, "color", e.target.value)} />
                    <input className="form-control form-control-sm" value={s.color}
                           onChange={(e) => onChange(idx, "color", e.target.value)}
                           style={{ width: 90 }} />
                  </div>
                </td>
                <td>
                  <select className="form-select form-select-sm"
                          value={payType}
                          onChange={(e) => onChange(idx, "pay_type", e.target.value)}
                          data-testid={`status-paytype-${idx}`}>
                    <option value="coefficient">{t("settings.statuses.pay_coefficient")}</option>
                    <option value="fixed">{t("settings.statuses.pay_fixed")}</option>
                  </select>
                </td>
                <td>
                  {payType === "fixed" ? (
                    <div className="input-group input-group-sm">
                      <input type="number" step="100" min="0"
                             className="form-control form-control-sm"
                             value={s.fixed_amount ?? 0}
                             onChange={(e) => onChange(idx, "fixed_amount", e.target.value)}
                             data-testid={`status-fixed-${idx}`} />
                      <span className="input-group-text">{t("settings.statuses.fixed_short")}</span>
                    </div>
                  ) : (
                    <div className="input-group input-group-sm">
                      <input type="number" step="0.1" min="0" max="2"
                             className="form-control form-control-sm" value={s.paid_factor}
                             onChange={(e) => onChange(idx, "paid_factor", e.target.value)}
                             data-testid={`status-paid-${idx}`} />
                      <span className="input-group-text">{t("settings.statuses.factor_short")}</span>
                    </div>
                  )}
                </td>
                <td>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => onRemove(idx)}
                          data-testid={`status-remove-${idx}`}
                          disabled={list.length <= 1}>
                    <i className="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button className="btn btn-primary mt-3" onClick={save} disabled={busy} data-testid="settings-save-statuses">
        <i className="bi bi-check-circle me-1"></i> {t("settings.statuses.save")}
      </button>
    </div></div>
  );
}

function TaxesTab({ company, refresh }) {
  const { t, i18n } = useTranslation();
  const [cfg, setCfg] = useState(TAX_DEFAULTS);
  const [example, setExample] = useState(500000);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const localeMap = { ru: "ru-RU", kk: "kk-KZ", en: "en-US" };
  const numLocale = localeMap[i18n.language] || "ru-RU";

  // Initialize: build taxes[] from server (already normalised via /auth/me).
  useEffect(() => {
    const raw = company?.tax_config || {};
    const c = { ...TAX_DEFAULTS, ...raw };
    if (!Array.isArray(c.taxes) || c.taxes.length === 0) {
      c.taxes = defaultTaxes();
    }
    if (!Array.isArray(c.deductions) || c.deductions.length === 0) {
      c.deductions = [{
        key: "mzp",
        label: t("settings.taxes.income_tax") === "Income tax" ? "1 MMW deduction" : "Вычет 1 МЗП",
        type: "absolute", value: c.mzpAmount ?? 85000, unit: "tenge", enabled: true,
      }];
    }
    c.deductions = c.deductions.map((d, i) => ({
      ...d,
      key: d.key || `d_${i}_${Date.now().toString(36)}`,
      unit: d.unit || "tenge",
    }));
    const allDedKeys = c.deductions.map(d => d.key);
    c.taxes = c.taxes.map(tx => ({
      ...tx,
      min_base_mzp: Number(tx.min_base_mzp) || 0,
      max_base_mzp: Number(tx.max_base_mzp) || 0,
      deduction_keys: Array.isArray(tx.deduction_keys)
        ? tx.deduction_keys
        : (tx.base === "after_deductions" ? [...allDedKeys] : []),
    }));
    if (!Array.isArray(c.ref_values) || c.ref_values.length === 0) {
      c.ref_values = DEFAULT_REF_VALUES.map(r => ({ ...r }));
    }
    setCfg(c);
  }, [company, t]);

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const payload = {
        ...cfg,
        taxes: (cfg.taxes || []).map(tx => ({
          ...tx,
          min_base_mzp: Math.max(0, Number(tx.min_base_mzp) || 0),
          max_base_mzp: Math.max(0, Number(tx.max_base_mzp) || 0),
          deduction_keys: tx.deduction_keys || [],
          base: (tx.deduction_keys || []).length ? "after_deductions" : "gross",
        })),
        ref_values: (cfg.ref_values || [])
          .map(r => ({ year: Number(r.year) || 0, mzp: Number(r.mzp) || 0, mrp: Number(r.mrp) || 0 }))
          .filter(r => r.year > 0),
      };
      await updateTaxes(payload); await refresh(); setMsg(t("settings.taxes.saved"));
    }
    finally { setBusy(false); }
  };

  // --- taxes CRUD ---
  const setTax = (idx, patch) => {
    const next = [...(cfg.taxes || [])];
    next[idx] = { ...next[idx], ...patch };
    setCfg({ ...cfg, taxes: next });
  };
  const addTax = () => {
    const next = [...(cfg.taxes || []), {
      key: `tax_${Date.now().toString(36)}`,
      label: t("settings.taxes.new_tax_label"),
      rate: 0,
      base: "gross",
      apply_to: "employee",
      enabled: true,
      min_base_mzp: 0,
      max_base_mzp: 0,
      deduction_keys: [],
    }];
    setCfg({ ...cfg, taxes: next });
  };
  const removeTax = (idx) => {
    const next = [...(cfg.taxes || [])];
    next.splice(idx, 1);
    setCfg({ ...cfg, taxes: next });
  };
  const toggleTaxDed = (idx, key) => {
    const cur = new Set((cfg.taxes?.[idx]?.deduction_keys) || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    setTax(idx, { deduction_keys: [...cur] });
  };

  // --- ref values (МЗП/МРП by year) CRUD ---
  const setRef = (idx, patch) => {
    const next = [...(cfg.ref_values || [])];
    next[idx] = { ...next[idx], ...patch };
    setCfg({ ...cfg, ref_values: next });
  };
  const addRef = () => {
    const list = cfg.ref_values || [];
    const maxYear = list.reduce((m, r) => Math.max(m, Number(r.year) || 0), 0);
    const last = list[list.length - 1] || {};
    setCfg({ ...cfg, ref_values: [...list, {
      year: (maxYear || new Date().getFullYear()) + 1,
      mzp: Number(last.mzp) || 85000,
      mrp: Number(last.mrp) || 4325,
    }]});
  };
  const removeRef = (idx) => {
    const next = [...(cfg.ref_values || [])];
    next.splice(idx, 1);
    setCfg({ ...cfg, ref_values: next });
  };

  // --- НК РК 2026 preset ---
  const applyPreset = () => {
    if (!window.confirm(t("settings.taxes.preset_confirm"))) return;
    setCfg({ ...cfg, ...NK_RK_2026_PRESET() });
    setMsg(t("settings.taxes.preset_done"));
  };

  // --- deductions CRUD ---
  const setDed = (idx, patch) => {
    const next = [...(cfg.deductions || [])];
    next[idx] = { ...next[idx], ...patch };
    setCfg({ ...cfg, deductions: next });
  };
  const addDed = () => {
    const next = [...(cfg.deductions || []), {
      key: `d_${Date.now()}`, label: t("settings.taxes.ded_label"),
      type: "absolute", value: 0, unit: "tenge", enabled: true,
    }];
    setCfg({ ...cfg, deductions: next });
  };
  const removeDed = (idx) => {
    const removed = (cfg.deductions || [])[idx];
    const next = [...(cfg.deductions || [])];
    next.splice(idx, 1);
    // Drop dangling references from taxes
    const taxes = (cfg.taxes || []).map(tx => ({
      ...tx,
      deduction_keys: (tx.deduction_keys || []).filter(k => k !== removed?.key),
    }));
    setCfg({ ...cfg, deductions: next, taxes });
  };

  const curYear = new Date().getFullYear();
  const refs = resolveRefs(cfg, curYear);
  const tax = computeTaxes(Number(example) || 0, cfg, null, { year: curYear });
  const enabledTaxes = (cfg.taxes || []).filter(x => x.enabled !== false);

  return (
    <div className="row g-3" data-testid="settings-taxes">
      <div className="col-lg-8"><div className="card card-soft"><div className="card-body">
        {msg && <div className="alert alert-success py-2">{msg}</div>}

        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-1">
          <h6 className="fw-semibold mb-0">{t("settings.taxes.rates_title")}</h6>
          <button className="btn btn-outline-success btn-sm" onClick={applyPreset} data-testid="tax-preset-2026">
            <i className="bi bi-magic me-1"></i> {t("settings.taxes.preset_btn")}
          </button>
        </div>
        <div className="text-muted small mb-3">{t("settings.taxes.rates_hint_universal")}</div>

        {(cfg.taxes || []).length === 0 ? (
          <div className="alert alert-light py-2 small mb-2">{t("settings.taxes.empty_taxes")}</div>
        ) : (
          <div className="table-responsive mb-2">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 130 }}>{t("settings.taxes.tax_name")}</th>
                  <th style={{ width: 135, minWidth: 135 }}>{t("settings.taxes.tax_rate")}</th>
                  <th style={{ minWidth: 180 }}>{t("settings.taxes.tax_deds")}</th>
                  <th style={{ width: 95 }}>{t("settings.taxes.tax_min_base")}</th>
                  <th style={{ width: 95 }}>{t("settings.taxes.tax_max_base")}</th>
                  <th style={{ width: 150 }}>{t("settings.taxes.tax_apply_to")}</th>
                  <th style={{ width: 60 }} className="text-center">{t("settings.taxes.tax_enabled")}</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {(cfg.taxes || []).map((tx, idx) => (
                  <tr key={tx.key} data-testid={`tax-row-${tx.key}`}>
                    <td>
                      <input className="form-control form-control-sm" value={tx.label ?? ""}
                             onChange={(e) => setTax(idx, { label: e.target.value })}
                             data-testid={`tax-label-${idx}`} />
                    </td>
                    <td>
                      <div className="input-group input-group-sm" style={{ minWidth: 120 }}>
                        <input type="number" className="form-control" step="0.01" min="0" max="100"
                               style={{ minWidth: 70 }}
                               value={tx.rate ?? 0}
                               onChange={(e) => setTax(idx, { rate: Number(e.target.value) })}
                               data-testid={`tax-rate-${idx}`} />
                        <span className="input-group-text">%</span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1 align-items-center">
                        {(cfg.deductions || []).map(d => {
                          const on = (tx.deduction_keys || []).includes(d.key);
                          return (
                            <label key={d.key}
                                   className={`badge ${on ? "bg-primary" : "bg-light text-muted border"}`}
                                   style={{ cursor: "pointer", userSelect: "none", fontWeight: 500 }}
                                   data-testid={`tax-ded-${idx}-${d.key}`}>
                              <input type="checkbox" className="visually-hidden" checked={on}
                                     onChange={() => toggleTaxDed(idx, d.key)} />
                              {on && <i className="bi bi-check-lg me-1"></i>}
                              {d.label || d.key}
                            </label>
                          );
                        })}
                        {((cfg.deductions || []).length === 0 || (tx.deduction_keys || []).length === 0) && (
                          <span className="small text-muted">{t("settings.taxes.tax_no_deds")}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <input type="number" className="form-control form-control-sm" step="0.5" min="0"
                             value={tx.min_base_mzp ?? 0}
                             onChange={(e) => setTax(idx, { min_base_mzp: Number(e.target.value) })}
                             data-testid={`tax-minbase-${idx}`} />
                    </td>
                    <td>
                      <input type="number" className="form-control form-control-sm" step="0.5" min="0"
                             value={tx.max_base_mzp ?? 0}
                             onChange={(e) => setTax(idx, { max_base_mzp: Number(e.target.value) })}
                             data-testid={`tax-maxbase-${idx}`} />
                    </td>
                    <td>
                      <select className="form-select form-select-sm" value={tx.apply_to || "employee"}
                              onChange={(e) => setTax(idx, { apply_to: e.target.value })}
                              data-testid={`tax-apply-${idx}`}>
                        <option value="employee">{t("settings.taxes.apply_employee")}</option>
                        <option value="employer">{t("settings.taxes.apply_employer")}</option>
                      </select>
                    </td>
                    <td className="text-center">
                      <input className="form-check-input" type="checkbox"
                             checked={tx.enabled !== false}
                             onChange={(e) => setTax(idx, { enabled: e.target.checked })}
                             data-testid={`tax-enabled-${idx}`} />
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => removeTax(idx)}
                              data-testid={`tax-remove-${idx}`} title={t("settings.taxes.tax_remove")}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="small text-muted mb-2">{t("settings.taxes.tax_base_limits_hint")}</div>
        <button className="btn btn-outline-primary btn-sm mb-4" onClick={addTax} data-testid="tax-add">
          <i className="bi bi-plus-lg me-1"></i> {t("settings.taxes.tax_add")}
        </button>

        <h6 className="fw-semibold mb-1">{t("settings.taxes.deductions_title")}</h6>
        <div className="text-muted small mb-2">{t("settings.taxes.deductions_hint")}</div>

        {(cfg.deductions || []).length === 0 ? (
          <div className="alert alert-light py-2 small mb-2">{t("settings.taxes.ded_empty")}</div>
        ) : (
          <div className="table-responsive mb-2">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t("settings.taxes.ded_label")}</th>
                  <th style={{ width: 150 }}>{t("settings.taxes.ded_type")}</th>
                  <th style={{ width: 130 }}>{t("settings.taxes.ded_value")}</th>
                  <th style={{ width: 95 }}>{t("settings.taxes.ded_unit")}</th>
                  <th style={{ width: 70 }} className="text-center">{t("settings.taxes.ded_enabled")}</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {(cfg.deductions || []).map((d, idx) => (
                  <tr key={idx} data-testid={`ded-row-${idx}`}>
                    <td>
                      <input className="form-control form-control-sm" value={d.label || ""}
                             onChange={(e) => setDed(idx, { label: e.target.value })}
                             data-testid={`ded-label-${idx}`} />
                    </td>
                    <td>
                      <select className="form-select form-select-sm"
                              value={d.type === "percent_gross" ? "percent_gross" : "absolute"}
                              onChange={(e) => setDed(idx, { type: e.target.value })}
                              data-testid={`ded-type-${idx}`}>
                        <option value="absolute">{t("settings.taxes.ded_type_absolute")}</option>
                        <option value="percent_gross">{t("settings.taxes.ded_type_percent")}</option>
                      </select>
                    </td>
                    <td>
                      <div className="input-group input-group-sm">
                        <input type="number" className="form-control" step="0.01" min="0" value={d.value ?? 0}
                               onChange={(e) => setDed(idx, { value: Number(e.target.value) })}
                               data-testid={`ded-value-${idx}`} />
                        <span className="input-group-text">
                          {d.type === "percent_gross" ? "%"
                            : d.unit === "mzp" ? t("settings.taxes.unit_mzp")
                            : d.unit === "mrp" ? t("settings.taxes.unit_mrp") : "₸"}
                        </span>
                      </div>
                    </td>
                    <td>
                      {d.type === "percent_gross" ? (
                        <span className="small text-muted">—</span>
                      ) : (
                        <select className="form-select form-select-sm" value={d.unit || "tenge"}
                                onChange={(e) => setDed(idx, { unit: e.target.value })}
                                data-testid={`ded-unit-${idx}`}>
                          <option value="tenge">₸</option>
                          <option value="mzp">{t("settings.taxes.unit_mzp")}</option>
                          <option value="mrp">{t("settings.taxes.unit_mrp")}</option>
                        </select>
                      )}
                    </td>
                    <td className="text-center">
                      <input className="form-check-input" type="checkbox"
                             checked={d.enabled !== false}
                             onChange={(e) => setDed(idx, { enabled: e.target.checked })}
                             data-testid={`ded-enabled-${idx}`} />
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => removeDed(idx)}
                              data-testid={`ded-remove-${idx}`} title={t("settings.taxes.ded_remove")}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button className="btn btn-outline-primary btn-sm" onClick={addDed} data-testid="ded-add">
          <i className="bi bi-plus-lg me-1"></i> {t("settings.taxes.ded_add")}
        </button>

        <h6 className="fw-semibold mb-1 mt-4">{t("settings.taxes.refs_title")}</h6>
        <div className="text-muted small mb-2">{t("settings.taxes.refs_hint")}</div>
        <div className="table-responsive mb-2" style={{ maxWidth: 480 }}>
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 110 }}>{t("settings.taxes.refs_year")}</th>
                <th>{t("settings.taxes.unit_mzp")}</th>
                <th>{t("settings.taxes.unit_mrp")}</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {(cfg.ref_values || []).map((r, idx) => (
                <tr key={idx} data-testid={`ref-row-${idx}`}>
                  <td>
                    <input type="number" className="form-control form-control-sm" min="2000" max="2100"
                           value={r.year ?? ""} onChange={(e) => setRef(idx, { year: Number(e.target.value) })}
                           data-testid={`ref-year-${idx}`} />
                  </td>
                  <td>
                    <input type="number" className="form-control form-control-sm" min="0" step="100"
                           value={r.mzp ?? 0} onChange={(e) => setRef(idx, { mzp: Number(e.target.value) })}
                           data-testid={`ref-mzp-${idx}`} />
                  </td>
                  <td>
                    <input type="number" className="form-control form-control-sm" min="0" step="1"
                           value={r.mrp ?? 0} onChange={(e) => setRef(idx, { mrp: Number(e.target.value) })}
                           data-testid={`ref-mrp-${idx}`} />
                  </td>
                  <td>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => removeRef(idx)}
                            data-testid={`ref-remove-${idx}`}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="btn btn-outline-primary btn-sm" onClick={addRef} data-testid="ref-add">
          <i className="bi bi-plus-lg me-1"></i> {t("settings.taxes.refs_add")}
        </button>

        <div className="mt-4">
          <button className="btn btn-primary" onClick={save} disabled={busy} data-testid="tax-save">
            <i className="bi bi-check-circle me-1"></i> {t("settings.taxes.save")}
          </button>
        </div>
      </div></div></div>

      <div className="col-lg-4"><div className="card card-soft"><div className="card-body">
        <h6 className="fw-semibold mb-2">{t("settings.taxes.example_title")}</h6>
        <div className="small text-muted mb-2" data-testid="tax-example-refs">
          {curYear} · {t("settings.taxes.unit_mzp")}: {refs.mzp.toLocaleString(numLocale)} ₸ · {t("settings.taxes.unit_mrp")}: {refs.mrp.toLocaleString(numLocale)} ₸
        </div>
        <label className="form-label small">{t("settings.taxes.example_amount")}</label>
        <input type="number" className="form-control mb-3" value={example}
               onChange={(e) => setExample(e.target.value)} data-testid="tax-example-input" />
        <table className="table table-sm">
          <tbody>
            <tr>
              <td>{t("settings.taxes.row_gross")}</td>
              <td className="text-end fw-semibold">{tax.gross.toLocaleString(numLocale)}</td>
            </tr>
            {enabledTaxes.filter(x => x.apply_to !== "employer").map(x => (
              <tr key={x.key}>
                <td>{x.label}</td>
                <td className="text-end">−{(tax.byTax[x.key]?.amount || 0).toLocaleString(numLocale)}</td>
              </tr>
            ))}
            <tr className="table-success">
              <td><strong>{t("settings.taxes.row_net")}</strong></td>
              <td className="text-end fw-bold">{tax.net.toLocaleString(numLocale)}</td>
            </tr>
            {enabledTaxes.filter(x => x.apply_to === "employer").map(x => (
              <tr key={x.key}>
                <td className="text-muted">{x.label} <small>({t("settings.taxes.apply_employer_short")})</small></td>
                <td className="text-end text-muted">{(tax.byTax[x.key]?.amount || 0).toLocaleString(numLocale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div></div></div>
    </div>
  );
}

function defaultTaxes() {
  return [
    { key: "opv",   label: "ОПВ",   rate: 10,  base: "gross",            apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: [] },
    { key: "vosms", label: "ВОСМС", rate: 2,   base: "gross",            apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: [] },
    { key: "ipn",   label: "ИПН",   rate: 10,  base: "after_deductions", apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: null },
    { key: "so",    label: "СО",    rate: 3.5, base: "gross",            apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: [] },
  ];
}

const DEFAULT_REF_VALUES = [
  { year: 2025, mzp: 85000, mrp: 3932 },
  { year: 2026, mzp: 85000, mrp: 4325 },
];

// Пресет «НК РК 2026»: ставки, пределы баз и вычеты по Налоговому кодексу РК на 2026 год.
//  - ОПВ 10% (раб.), база ≤ 50 МЗП            - ОПВР 3,5% (работодатель), база ≤ 50 МЗП
//  - ВОСМС 2% (раб.), база ≤ 20 МЗП           - ООСМС 3% (работодатель), база ≤ 40 МЗП
//  - ИПН 10% (раб.), база = доход − ОПВ − ВОСМС − 30 МРП
//  - СО 5% (работодатель), база = доход − ОПВ, мин 1 МЗП, макс 7 МЗП
//  - Соцналог 6% (работодатель), база = доход − ОПВ − ВОСМС
function NK_RK_2026_PRESET() {
  return {
    ref_values: DEFAULT_REF_VALUES.map(r => ({ ...r })),
    deductions: [
      { key: "opv_pct",   label: "Вычет ОПВ",                  type: "percent_gross", value: 10, unit: "tenge", enabled: true },
      { key: "vosms_pct", label: "Вычет ВОСМС",                type: "percent_gross", value: 2,  unit: "tenge", enabled: true },
      { key: "std_mrp",   label: "Стандартный вычет (30 МРП)", type: "absolute",      value: 30, unit: "mrp",   enabled: true },
    ],
    taxes: [
      { key: "opv",   label: "ОПВ",      rate: 10,  base: "gross",            apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 50, deduction_keys: [] },
      { key: "vosms", label: "ВОСМС",    rate: 2,   base: "gross",            apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 20, deduction_keys: [] },
      { key: "ipn",   label: "ИПН",      rate: 10,  base: "after_deductions", apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 0,  deduction_keys: ["opv_pct", "vosms_pct", "std_mrp"] },
      { key: "so",    label: "СО",       rate: 5,   base: "after_deductions", apply_to: "employer", enabled: true, min_base_mzp: 1, max_base_mzp: 7,  deduction_keys: ["opv_pct"] },
      { key: "oosms", label: "ООСМС",    rate: 3,   base: "gross",            apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 40, deduction_keys: [] },
      { key: "opvr",  label: "ОПВР",     rate: 3.5, base: "gross",            apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 50, deduction_keys: [] },
      { key: "sn",    label: "Соцналог", rate: 6,   base: "after_deductions", apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 0,  deduction_keys: ["opv_pct", "vosms_pct"] },
    ],
  };
}


function LocationsTab() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reload = async () => {
    setLoading(true);
    try { setList(await listLocations() || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const startCreate = () => {
    setEditing(null); setName(""); setAddress(""); setCode(""); setErr(""); setShowForm(true);
  };
  const startEdit = (loc) => {
    setEditing(loc); setName(loc.name || ""); setAddress(loc.address || ""); setCode(loc.short_code || "");
    setErr(""); setShowForm(true);
  };

  const save = async () => {
    if (!name.trim()) { setErr(t("settings.locations.err_name")); return; }
    setBusy(true); setErr("");
    try {
      if (editing) {
        await updateLocationApi(editing.location_id, { name: name.trim(), address: address.trim(), short_code: code.trim() });
      } else {
        await createLocationApi({ name: name.trim(), address: address.trim(), short_code: code.trim() });
      }
      setShowForm(false);
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (loc) => {
    if (!window.confirm(t("settings.locations.confirm_delete", { name: loc.name }))) return;
    try { await deleteLocationApi(loc.location_id); await reload(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="settings-locations">
      <div className="card card-soft"><div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h6 className="fw-semibold mb-1">{t("settings.locations.title")}</h6>
            <div className="text-muted small">{t("settings.locations.hint")}</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={startCreate} data-testid="location-add">
            <i className="bi bi-plus-lg me-1"></i> {t("settings.locations.add")}
          </button>
        </div>

        {loading ? (
          <div className="text-center text-muted py-4">
            <div className="spinner-border spinner-border-sm me-2"></div>
            {t("common.loading")}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-geo-alt fs-1 d-block mb-2"></i>
            {t("settings.locations.empty")}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle mb-0 tabel-compact">
              <thead className="table-light">
                <tr>
                  <th>{t("settings.locations.f_name")}</th>
                  <th>{t("settings.locations.f_code")}</th>
                  <th>{t("settings.locations.f_address")}</th>
                  <th className="text-end">{t("settings.locations.f_employees")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map(loc => (
                  <tr key={loc.location_id} data-testid={`location-row-${loc.location_id}`}>
                    <td className="fw-semibold">{loc.name}</td>
                    <td className="text-muted small">{loc.short_code || "—"}</td>
                    <td className="text-muted small">{loc.address || "—"}</td>
                    <td className="text-end">
                      <span className="badge bg-light text-dark border">{loc.employees_count || 0}</span>
                    </td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => startEdit(loc)}
                              data-testid={`location-edit-${loc.location_id}`}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(loc)}
                              data-testid={`location-delete-${loc.location_id}`}
                              disabled={(loc.employees_count || 0) > 0}
                              title={(loc.employees_count || 0) > 0 ? t("settings.locations.cannot_delete") : ""}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div></div>

      {showForm && (
        <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-geo-alt me-2 text-primary"></i>
                  {editing ? t("settings.locations.edit_title") : t("settings.locations.add_title")}
                </h5>
                <button className="btn-close" onClick={() => setShowForm(false)}></button>
              </div>
              <div className="modal-body">
                {err && <div className="alert alert-danger py-2 small">{err}</div>}
                <div className="mb-3">
                  <label className="form-label">{t("settings.locations.f_name")} <span className="text-danger">*</span></label>
                  <input className="form-control" value={name} onChange={(e) => setName(e.target.value)}
                         placeholder={t("settings.locations.name_placeholder")}
                         data-testid="location-name-input" autoFocus />
                </div>
                <div className="mb-3">
                  <label className="form-label">{t("settings.locations.f_code")}</label>
                  <input className="form-control" value={code} onChange={(e) => setCode(e.target.value)}
                         placeholder="ALM-1" data-testid="location-code-input" maxLength={20} />
                  <div className="small text-muted mt-1">{t("settings.locations.code_hint")}</div>
                </div>
                <div className="mb-2">
                  <label className="form-label">{t("settings.locations.f_address")}</label>
                  <input className="form-control" value={address} onChange={(e) => setAddress(e.target.value)}
                         placeholder={t("settings.locations.address_placeholder")}
                         data-testid="location-address-input" />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>{t("common.cancel")}</button>
                <button className="btn btn-primary" disabled={busy} onClick={save} data-testid="location-save">
                  <i className="bi bi-check2 me-1"></i> {t("common.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// SOCIAL STATUSES TAB — tax exemptions per employee social_status
// =========================================================================
function SocialStatusesTab({ company, refresh }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // Snapshot of company's taxes — these are the keys you can exempt from
  const companyTaxes = (company?.tax_config?.taxes || []).filter(t => t.enabled !== false);

  useEffect(() => {
    setItems((company?.social_statuses || []).map(s => ({ ...s, non_taxable_keys: [...(s.non_taxable_keys || [])] })));
  }, [company]);

  const addItem = () => {
    setItems([...items, { id: "", name: "", non_taxable_keys: [], enabled: true }]);
  };
  const updateItem = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    setItems(next);
  };
  const removeItem = (idx) => {
    if (!window.confirm(t("social_status.confirm_delete"))) return;
    const next = [...items];
    next.splice(idx, 1);
    setItems(next);
  };
  const toggleTax = (idx, key) => {
    const current = new Set(items[idx].non_taxable_keys || []);
    if (current.has(key)) current.delete(key);
    else current.add(key);
    updateItem(idx, { non_taxable_keys: [...current] });
  };

  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const payload = items.map(s => ({
        id: s.id || "",
        name: (s.name || "").trim(),
        non_taxable_keys: s.non_taxable_keys || [],
        enabled: s.enabled !== false,
      }));
      await updateSocialStatuses(payload);
      await refresh();
      setMsg(t("social_status.saved"));
    } catch (e) {
      setErr(e.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="social-statuses-tab">
      <div className="alert alert-light border py-2 small">
        <i className="bi bi-info-circle me-1"></i>
        {t("social_status.intro")}
      </div>

      {err && <div className="alert alert-danger py-2" data-testid="ss-error">{err}</div>}
      {msg && <div className="alert alert-success py-2" data-testid="ss-success">{msg}</div>}

      {companyTaxes.length === 0 && (
        <div className="alert alert-warning py-2 small">
          <i className="bi bi-exclamation-triangle me-1"></i>
          {t("social_status.no_taxes_warning")}
        </div>
      )}

      <div className="table-responsive">
        <table className="table align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: "30%" }}>{t("social_status.col_name")}</th>
              <th>{t("social_status.col_exempt_taxes")}</th>
              <th style={{ width: 80 }} className="text-center">{t("social_status.col_enabled")}</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="4" className="text-center text-muted py-3">{t("social_status.empty")}</td></tr>
            )}
            {items.map((s, idx) => (
              <tr key={idx} data-testid={`ss-row-${idx}`}>
                <td>
                  <input className="form-control form-control-sm" value={s.name}
                         onChange={(e) => updateItem(idx, { name: e.target.value })}
                         placeholder={t("social_status.name_ph")}
                         data-testid={`ss-name-${idx}`} />
                </td>
                <td>
                  <div className="d-flex flex-wrap gap-2">
                    {companyTaxes.map(tx => {
                      const isExempt = (s.non_taxable_keys || []).includes(tx.key);
                      return (
                        <label key={tx.key}
                               className={`badge ${isExempt ? "bg-warning text-dark" : "bg-light text-muted border"}`}
                               style={{ cursor: "pointer", userSelect: "none", padding: "6px 10px", fontWeight: 500 }}
                               data-testid={`ss-tax-${idx}-${tx.key}`}>
                          <input type="checkbox" className="visually-hidden"
                                 checked={isExempt}
                                 onChange={() => toggleTax(idx, tx.key)} />
                          {isExempt && <i className="bi bi-check-lg me-1"></i>}
                          {tx.label}
                          <small className="ms-1 opacity-75">({tx.rate}%)</small>
                        </label>
                      );
                    })}
                  </div>
                  <div className="small text-muted mt-1">
                    {t("social_status.hint")}
                  </div>
                </td>
                <td className="text-center">
                  <div className="form-check form-switch d-inline-block">
                    <input className="form-check-input" type="checkbox"
                           checked={s.enabled !== false}
                           onChange={(e) => updateItem(idx, { enabled: e.target.checked })}
                           data-testid={`ss-enabled-${idx}`} />
                  </div>
                </td>
                <td className="text-end">
                  <button className="btn btn-sm btn-outline-danger" onClick={() => removeItem(idx)}
                          data-testid={`ss-remove-${idx}`}>
                    <i className="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex gap-2">
        <button className="btn btn-outline-primary" onClick={addItem} data-testid="ss-add">
          <i className="bi bi-plus-circle me-1"></i> {t("social_status.add")}
        </button>
        <button className="btn btn-primary ms-auto" onClick={save} disabled={busy}
                data-testid="ss-save">
          <i className="bi bi-check2 me-1"></i>
          {busy ? t("common.saving") : t("social_status.save")}
        </button>
      </div>
    </div>
  );
}

// =========================================================================
// WITHHOLDING KINDS TAB — справочник типов удержаний
// =========================================================================
function WithholdingKindsTab({ company, refresh }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    setItems(allWithholdingKindDefs(company).map((k) => ({ ...k })));
  }, [company]);

  const addItem = () => {
    setItems([...items, { key: "", name: "", limit_pct: 20, enabled: true, builtin: false }]);
  };
  const updateItem = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    setItems(next);
  };
  const removeItem = (idx) => {
    if (items[idx]?.builtin) return;
    if (!window.confirm(t("withholding_kinds.confirm_delete"))) return;
    const next = [...items];
    next.splice(idx, 1);
    setItems(next);
  };

  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const payload = items.map((k) => ({
        key: k.key || slugifyKey(k.name),
        name: (k.name || "").trim(),
        limit_pct: Number(k.limit_pct) || 20,
        enabled: k.enabled !== false,
      }));
      await updateWithholdingKinds(payload);
      await refresh();
      setMsg(t("withholding_kinds.saved"));
    } catch (e) {
      setErr(e.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="withholding-kinds-tab">
      <div className="alert alert-light border py-2 small">
        <i className="bi bi-info-circle me-1"></i>
        {t("withholding_kinds.intro")}
      </div>

      {err && <div className="alert alert-danger py-2">{err}</div>}
      {msg && <div className="alert alert-success py-2">{msg}</div>}

      <div className="table-responsive">
        <table className="table align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: "28%" }}>{t("withholding_kinds.col_name")}</th>
              <th style={{ width: "18%" }}>{t("withholding_kinds.col_key")}</th>
              <th style={{ width: "14%" }}>{t("withholding_kinds.col_limit")}</th>
              <th style={{ width: 80 }} className="text-center">{t("withholding_kinds.col_enabled")}</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="5" className="text-center text-muted py-3">{t("withholding_kinds.empty")}</td></tr>
            )}
            {items.map((k, idx) => (
              <tr key={k.key || `new-${idx}`} data-testid={`wk-row-${idx}`}>
                <td>
                  <input className="form-control form-control-sm" value={k.name}
                         onChange={(e) => updateItem(idx, { name: e.target.value })}
                         placeholder={t("withholding_kinds.name_ph")} />
                </td>
                <td>
                  {k.builtin ? (
                    <code className="small text-muted">{k.key}</code>
                  ) : (
                    <input className="form-control form-control-sm" value={k.key}
                           onChange={(e) => updateItem(idx, { key: e.target.value })}
                           placeholder="custom_key" />
                  )}
                </td>
                <td>
                  <div className="input-group input-group-sm">
                    <input type="number" className="form-control" min="1" max="100"
                           value={k.limit_pct}
                           onChange={(e) => updateItem(idx, { limit_pct: e.target.value })} />
                    <span className="input-group-text">%</span>
                  </div>
                </td>
                <td className="text-center">
                  <div className="form-check form-switch d-inline-block">
                    <input className="form-check-input" type="checkbox"
                           checked={k.enabled !== false}
                           onChange={(e) => updateItem(idx, { enabled: e.target.checked })} />
                  </div>
                </td>
                <td className="text-end">
                  {!k.builtin && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeItem(idx)}>
                      <i className="bi bi-trash"></i>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex gap-2">
        <button type="button" className="btn btn-outline-primary" onClick={addItem}>
          <i className="bi bi-plus-circle me-1"></i> {t("withholding_kinds.add")}
        </button>
        <button type="button" className="btn btn-primary ms-auto" onClick={save} disabled={busy}>
          <i className="bi bi-check2 me-1"></i>
          {busy ? t("common.saving") : t("withholding_kinds.save")}
        </button>
      </div>
    </div>
  );
}

